import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Download, Mail, Star, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { PublicCourse, UtilityPageSummary } from '../services/publicService';
import { publicService } from '../services/publicService';
import { UtilityMetadata } from '../types';
import { formatPrice } from '../lib/format';
import { thumbnailSrc } from '../lib/imageUrl';
import { onThumbError } from './ui/imageFallback';

interface UtilityBridgeProps {
  parentCourse: PublicCourse;
  ctaText: string;
  ctaDescription: string;
  leadMagnet?: UtilityMetadata['leadMagnet'];
  relatedUtilityPages?: UtilityPageSummary[];
}

export const UtilityBridge: React.FC<UtilityBridgeProps> = ({
  parentCourse,
  ctaText,
  ctaDescription,
  leadMagnet,
  relatedUtilityPages,
}) => {
  const [email, setEmail] = useState('');
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLeadCapture = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await publicService.createLead({
        email,
        source: 'lead_magnet',
        sourceCourseId: parentCourse.id,
      });
      setLeadCaptured(true);
    } catch (err) {
      console.error('Lead capture failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Main CTA Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-br from-primary-50 via-white to-primary-50 border border-primary-100 rounded-2xl p-8 md:p-10"
      >
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
            <BookOpen className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              {ctaText}
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {ctaDescription}
            </p>
          </div>
        </div>

        {/* Course Preview Card */}
        <Link
          to={`/course/${parentCourse.id}`}
          className="block group"
        >
          <div className="flex items-center gap-5 bg-white rounded-xl p-5 border border-gray-100 hover:border-primary-200 hover:shadow-card-hover transition-all duration-300">
            <img
              src={thumbnailSrc(parentCourse.thumbnailUrl, 'list')}
              onError={onThumbError}
              alt={parentCourse.title}
              className="w-20 h-14 rounded-lg object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                {parentCourse.title}
              </h4>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                <span className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-yellow-500 fill-current" />
                  {parentCourse.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {parentCourse.totalStudents.toLocaleString()} students
                </span>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <span className="block text-lg font-bold text-primary-700">
                {parentCourse.price === 0 ? 'Free' : formatPrice(parentCourse.price)}
              </span>
              <ArrowRight className="h-4 w-4 text-primary-400 ml-auto mt-1 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
      </motion.div>

      {/* Lead Magnet Section */}
      {leadMagnet && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white border border-gray-200 rounded-2xl p-8"
        >
          {leadCaptured ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                <Download className="h-6 w-6 text-green-600" />
              </div>
              <h4 className="font-bold text-gray-900 mb-1">Check your inbox!</h4>
              <p className="text-sm text-gray-500">
                We've sent the {leadMagnet.title.toLowerCase()} to {email}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-4 mb-5">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Download className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">{leadMagnet.title}</h4>
                  <p className="text-sm text-gray-500 mt-0.5">{leadMagnet.description}</p>
                </div>
              </div>
              <form onSubmit={handleLeadCapture} className="flex gap-3">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full pl-10 pr-4 py-3 text-sm bg-gray-50 border border-gray-200 rounded-lg placeholder-gray-400 focus:ring-2 focus:ring-primary-100 focus:border-primary-300 transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 py-3 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex-shrink-0"
                >
                  {loading ? 'Sending...' : 'Get it free'}
                </button>
              </form>
            </>
          )}
        </motion.div>
      )}

      {/* Related Utility Pages */}
      {relatedUtilityPages && relatedUtilityPages.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
            More free guides
          </h4>
          <div className="space-y-3">
            {relatedUtilityPages.map(page => (
              <Link
                key={page.id}
                to={`/learn-free/${page.slug}`}
                className="flex items-center gap-4 p-4 bg-white border border-gray-100 rounded-xl hover:border-gray-200 hover:shadow-sm transition-all group"
              >
                <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="h-4 w-4 text-primary-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {page.title}
                  </span>
                  <span className="text-xs text-gray-400 ml-2">
                    {page.slideCount} slides · {page.estimatedMinutes} min
                  </span>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-primary-400 group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};
